import type { NotificationAction } from '@houzy/schemas/partner-cockpit/notifications';
import CloseIcon from '@mui/icons-material/Close';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { match, P } from 'ts-pattern';

import { ButtonLink } from '../../../components/ButtonLink';
import { stateColor } from '../../../theme';
import { notificationLinkOptions } from '../../notifications/utils/notificationLink';
import type { PartnerCockpitBanner } from '../api/announcementsApi';

export type AnnouncementBannerProps = {
    banner: PartnerCockpitBanner;
    onDismiss?: () => void;
};

// Only the static (no-entity-uuid) action targets make sense on a broadcast — openRequestDetail has no
// single partner-specific entity to point every recipient at, so it gets no label and never renders here.
function actionLabelKey(action: NotificationAction | null): string | undefined {
    if (!action) {
        return undefined;
    }
    switch (action.target) {
        case 'teamMembers':
            return 'ANNOUNCEMENTS.ACTION.TEAM_MEMBERS';
        case 'companySettings':
            return 'ANNOUNCEMENTS.ACTION.COMPANY_SETTINGS';
        case 'verification':
            return 'ANNOUNCEMENTS.ACTION.VERIFICATION';
        case 'externalLink':
            return 'ANNOUNCEMENTS.ACTION.MORE_INFORMATION';
        case 'openRequestDetail':
            return undefined;
    }
}

export function AnnouncementBanner({ banner, onDismiss }: AnnouncementBannerProps): ReactElement {
    const { t } = useTranslation();
    const showClose = Boolean(banner.dismissable && onDismiss);
    const linkTarget = notificationLinkOptions(banner.action ?? undefined);
    const actionLabel = actionLabelKey(banner.action);
    const actionButton = actionLabel
        ? match(linkTarget)
              .with({ type: 'internal' }, (target) => (
                  <ButtonLink
                      {...target.options}
                      color="inherit"
                      size="small"
                      sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {t(actionLabel)}
                  </ButtonLink>
              ))
              .with({ type: 'external' }, (target) => (
                  <Button
                      component="a"
                      href={target.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      color="inherit"
                      size="small"
                      sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {t(actionLabel)}
                  </Button>
              ))
              .with(P.nullish, () => undefined)
              .exhaustive()
        : undefined;

    return (
        <Alert
            severity={banner.severity}
            icon={banner.content.icon ? <span aria-hidden>{banner.content.icon}</span> : undefined}
            action={
                actionButton || showClose ? (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                        {actionButton}
                        {showClose ? (
                            <IconButton
                                size="small"
                                color="inherit"
                                onClick={onDismiss}
                                aria-label={t('VALIDATION_BANNER.DISMISS')}>
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        ) : undefined}
                    </Stack>
                ) : undefined
            }
            sx={(theme) => ({
                alignItems: 'flex-start',
                border: 1,
                borderColor: stateColor(theme, 'outlinedBorder', banner.severity),
            })}>
            <AlertTitle>{t(banner.content.titleKey, banner.content.params)}</AlertTitle>
            {t(banner.content.descriptionKey, banner.content.params)}
        </Alert>
    );
}
